// src/pages/SalaryAdjustments.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiUrl } from '../config/api';
import { getSriLankaCurrentMonth, getSriLankaCurrentYear } from '../lib/sriLankaTime';

const API_LIST     = apiUrl('get_salary_adjustments');
const API_SAVE     = apiUrl('save_salary_adjustment');
const API_DELETE   = apiUrl('delete_salary_adjustment');
const API_STAFF    = apiUrl('get_staff');
const toArray = (value) => (Array.isArray(value) ? value : []);

const TYPE_OPTIONS = [
  { label: "Bonus",  value: "bonus"  },
  { label: "Faulty", value: "faulty" },
];

export default function SalaryAdjustments() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ─ Filters & Data ────────────────────────
  const [year, setYear]         = useState(() => getSriLankaCurrentYear());
  const [month, setMonth]       = useState(() => getSriLankaCurrentMonth());
  const [staffFilter, setStaffFilter] = useState('');
  const [records, setRecords]   = useState([]);
  const [staffList, setStaffList] = useState([]);

  // ─ Loading / Saving ──────────────────────
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);

  // ─ Modal & Form ──────────────────────────
  const [openModal, setOpenModal] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [form, setForm] = useState({
    id: null,
    year,
    month,
    staff_id: '',
    type: 'bonus',
    amount: '',
    note: ''
  });

  // ─ Fetch staff on mount ───────────────────
  useEffect(() => {
    fetch(API_STAFF)
      .then(r => r.json())
      .then((payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
      })
      .then(setStaffList)
      .catch((err) => {
        console.error(err);
        setStaffList([]);
      });
  }, []);

  // ─ Fetch records when filters change ─────
  useEffect(fetchRecords, [year, month, staffFilter]);

  function fetchRecords() {
    setLoading(true);
    const qs = new URLSearchParams({
      year: String(year),
      month: String(month).padStart(2, '0'),
      staff_id: staffFilter || ''
    }).toString();
    fetch(`${API_LIST}?${qs}`)
      .then(r => r.json())
      .then((payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
      })
      .then(setRecords)
      .catch((err) => {
        console.error(err);
        setRecords([]);
      })
      .finally(() => setLoading(false));
  }

  const safeRecords = toArray(records);
  const safeStaffList = toArray(staffList);

  // ─ Open / Close Modal ────────────────────
  function openAdd() {
    setForm({ id:null, year, month, staff_id:'', type:'bonus', amount:'', note:'' });
    setEditIndex(-1);
    setOpenModal(true);
  }
  function openEdit(idx) {
    const r = safeRecords[idx];
    if (!r) return;
    setForm({
      id: r.id,
      year: r.year,
      month: r.month,
      staff_id: r.staff_id,
      type: r.type,
      amount: r.amount,
      note: r.note || ''
    });
    setEditIndex(idx);
    setOpenModal(true);
  }
  function closeModal() {
    setOpenModal(false);
  }

  // ─ Handle form change ────────────────────
  function onFormChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  // ─ Save (add/update) ─────────────────────
  function saveRecord() {
    if (!form.staff_id || !form.amount) {
      return alert("Please select a staff member and enter an amount.");
    }
    setSaving(true);
    fetch(API_SAVE, {
      method: 'POST',
      headers: {'Content-Type':'application/json'},
      body: JSON.stringify(form)
    })
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') {
          fetchRecords();
          closeModal();
        } else {
          alert("Error: " + d.message);
        }
      })
      .catch(() => alert("Network error"))
      .finally(() => setSaving(false));
  }

  // ─ Delete ────────────────────────────────
  function deleteRecord(id) {
    if (!window.confirm("Delete this record?")) return;
    fetch(API_DELETE, {
      method:'POST',
      headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ id })
    })
      .then(r => r.json())
      .then(d => {
        if (d.status==='success') fetchRecords();
        else alert("Error: "+d.message);
      })
      .catch(() => alert("Network error"));
  }

  // ─ Summary for selected staff ─────────────
  const filtered = safeRecords.filter(r => !staffFilter || r.staff_id == staffFilter);
  const totalBonus  = filtered
    .filter(r => r.type === 'bonus')
    .reduce((s,r) => s + parseFloat(r.amount||0), 0);
  const totalFaulty = filtered
    .filter(r => r.type === 'faulty')
    .reduce((s,r) => s + parseFloat(r.amount||0), 0);
  const netAdjust   = totalBonus - totalFaulty;

  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>
        Salary Adjustments
      </Typography>

      {/* Filters */}
      <Box sx={{ display:'flex', gap:2, mb:2, flexWrap:'wrap', alignItems:'center' }}>
        <TextField
          label="Year"
          type="number"
          size="small"
          value={year}
          onChange={e => setYear(+e.target.value)}
        />
        <TextField
          label="Month"
          type="number"
          size="small"
          inputProps={{ min:1, max:12 }}
          value={month}
          onChange={e => setMonth(+e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Staff</InputLabel>
          <Select
            label="Staff"
            value={staffFilter}
            onChange={e => setStaffFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">— All —</MenuItem>
            {safeStaffList.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={fetchRecords}>
          <RefreshIcon />
        </IconButton>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
        >
          Add Adjustment
        </Button>
      </Box>

      {/* Staff Summary */}
      {staffFilter && (
        <Paper sx={{ p:2, mb:2, display:'flex', gap:4 }}>
          {[
            { label:'Total Bonus',   value:totalBonus,  color:'green' },
            { label:'Total Faulty',  value:totalFaulty, color:'red'   },
            { label:'Net Adjust',    value:netAdjust,   color: netAdjust>=0?'green':'red' },
          ].map(stat => (
            <Box key={stat.label} sx={{ textAlign:'center', minWidth:120 }}>
              <Typography variant="subtitle2">{stat.label}</Typography>
              <Typography variant="h6" sx={{ color: stat.color }}>
                {stat.value>=0?'+':''}{stat.value.toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* List */}
      {loading
        ? <Box sx={{ textAlign:'center', py:4 }}><CircularProgress/></Box>
        : filtered.length === 0
          ? <Typography>No records found.</Typography>
          : isMobile
            ? (
              <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                {filtered.map((r,i) => (
                  <Paper key={r.id} sx={{ p:2, 
                    borderLeft: `4px solid ${r.type==='bonus'?'green':'red'}` 
                  }}>
                    <Typography>
                      <strong>{r.staff_name}</strong> —{' '}
                      <span style={{ color: r.type==='bonus'?'green':'red' }}>
                        {r.type==='bonus'?'+':''}{r.amount.toFixed(2)}
                      </span>
                    </Typography>
                    {r.note && <Typography variant="caption">{r.note}</Typography>}
                    <Box sx={{ mt:1 }}>
                      <Button size="small" onClick={()=>openEdit(i)}>Edit</Button>
                      <Button size="small" color="error" onClick={()=>deleteRecord(r.id)}>Delete</Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )
            : (
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Staff</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map(r => (
                      <TableRow key={r.id}>
                        <TableCell>{r.staff_name}</TableCell>
                        <TableCell>{r.type}</TableCell>
                        <TableCell align="right" sx={{
                          color: r.type==='bonus'?'green':'red'
                        }}>
                          {r.type==='bonus'?'+':''}{r.amount.toFixed(2)}
                        </TableCell>
                        <TableCell>{r.note}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={()=>openEdit(safeRecords.indexOf(r))}>
                            <EditIcon fontSize="small"/>
                          </IconButton>
                          <IconButton size="small" color="error" onClick={()=>deleteRecord(r.id)}>
                            <DeleteIcon fontSize="small"/>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )
      }

      {/* Add/Edit Dialog */}
      <Dialog open={openModal} onClose={closeModal} fullWidth>
        <DialogTitle>
          {editIndex>=0 ? "Edit Adjustment" : "Add Adjustment"}
        </DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2, pt:2 }}>
          <Box sx={{ display:'flex', gap:2 }}>
            <TextField
              label="Year"
              name="year"
              type="number"
              value={form.year}
              onChange={onFormChange}
              fullWidth
            />
            <TextField
              label="Month"
              name="month"
              type="number"
              inputProps={{ min:1, max:12 }}
              value={form.month}
              onChange={onFormChange}
              fullWidth
            />
          </Box>

          <TextField
            select
            label="Staff"
            name="staff_id"
            value={form.staff_id}
            onChange={onFormChange}
            fullWidth
          >
            {safeStaffList.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Type"
            name="type"
            value={form.type}
            onChange={onFormChange}
            fullWidth
          >
            {TYPE_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Amount"
            name="amount"
            type="number"
            value={form.amount}
            onChange={onFormChange}
            fullWidth
          />

          <TextField
            label="Note"
            name="note"
            value={form.note}
            onChange={onFormChange}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveRecord}
            disabled={saving}
            startIcon={saving && <CircularProgress size={20}/>}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
